import {
  Box,
  Button,
  Card,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import { PickList } from '../../components/PickList'
import {
  useCreateExtension,
  useGetExtensionInfo,
  useListExtensions,
  usePbxPlaylists,
  useUpdateExtension,
  useUpdateRingGroup,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { RingGroupSeedFields } from '../../types/extension.types'
import {
  EXTENSION_TYPE_DEFAULT_NUMBER,
  getNextExtensionNumber,
  isPlayablePlaylist,
  isReservedExtensionNumber,
  normalizeRingGroupStrategy,
  RING_GROUP_MEMBER_LIMIT,
  RING_STRATEGY_OPTIONS,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
} from '../../utils'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

export const RingGroupEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId =
    !isAddMode && extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading,
    isError,
  } = useGetExtensionInfo({
    extensionId: extensionId!,
    options: { enabled: isValidId },
  })

  const { extensions: lines } = useListExtensions({
    pbxId: pbxIdNum,
    listParams: {
      type: ExtensionType.LINE,
      statusFilter: 'active',
      fetchAll: true,
      limit: 1000,
    },
    options: { enabled: true },
  })
  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
      options: { enabled: isAddMode },
    })
  const { playlists } = usePbxPlaylists(pbxIdNum)

  const updateExtensionMutation = useUpdateExtension(pbxIdNum)
  const updateRingGroupMutation = useUpdateRingGroup(pbxIdNum)
  const createExtensionMutation = useCreateExtension(pbxIdNum)

  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(isAddMode)
  const [ringStrategy, setRingStrategy] = useState('Ring All')
  const [ringGroupPlaylistId, setRingGroupPlaylistId] = useState<string>('')
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.RING_GROUP)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER['ring-group'] ?? 200,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, isUkEnvironment])

  useEffect(() => {
    if (isAddMode) {
      setName(
        (prev) => prev || text('extensions.types.ringGroup', 'Ring Group'),
      )
      return
    }
    if (extension) {
      setName(extension.name ?? '')
      setNumber(extension.number ?? '')
      if (extension.type === ExtensionType.RING_GROUP) {
        const rg = extension as ExtensionInstance & RingGroupSeedFields
        setOmitFromDirectory(rg.omit_from_directory ?? false)
        setRingStrategy(normalizeRingGroupStrategy(rg.ring_strategy))
        setRingGroupPlaylistId(
          rg.custom_ringback_audio_id != null
            ? String(rg.custom_ringback_audio_id)
            : '',
        )
        setMemberIds(rg.members?.map((member) => member.id) ?? [])
      }
    }
  }, [extension, isAddMode])

  const nameInvalid = attemptedSubmit && !name.trim()
  const numberInvalid = attemptedSubmit && !number.trim()
  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)
  const ringbackPlaylistOptions = [
    {
      value: '',
      label: text('extensions.edit.ringGroup.ringing', 'Ringing'),
      disabled: false,
    },
    ...playlists.map((playlist) => {
      const playable = isPlayablePlaylist(playlist)
      return {
        value: String(playlist.id),
        label: playable
          ? playlist.name
          : text(
              'extensions.edit.playlistNotPlayableOption',
              '{{name}} (no audio tracks)',
              { name: playlist.name },
            ),
        disabled: !playable,
      }
    }),
  ]

  const allLineItems = lines
    .filter((line) => line.type === ExtensionType.LINE)
    .map((line) => ({ id: line.id, label: `${line.number} — ${line.name}` }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSubmit(true)
    if (!name.trim() || !number.trim()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.nameNumberRequired',
          'Name and number are required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    // Only guard when the playlist was actually changed. A persisted playlist
    // that later became empty still renders as selected (disabled but
    // pre-selected), so enforcing unconditionally would hard-block saves of
    // unrelated fields for a ring group that already points at it.
    const rg = extension as
      (ExtensionInstance & RingGroupSeedFields) | undefined
    const loadedPlaylistId =
      rg?.custom_ringback_audio_id != null
        ? String(rg.custom_ringback_audio_id)
        : ''
    const playlistChanged = ringGroupPlaylistId.trim() !== loadedPlaylistId
    const selectedPlaylist = playlists.find(
      (p) => String(p.id) === ringGroupPlaylistId.trim(),
    )
    if (
      playlistChanged &&
      selectedPlaylist &&
      !isPlayablePlaylist(selectedPlaylist)
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.playlistEmptyError',
          'The selected ringback playlist has no audio tracks. Add tracks or choose a different playlist.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    const normalizedNumber = number.trim()
    if (
      isReservedExtensionNumber(normalizedNumber, { isUkEnvironment }) &&
      (isAddMode || normalizedNumber !== (extension?.number ?? '').trim())
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.reservedNumber',
          '{{number}} is reserved. Please select a different number.',
          { number: normalizedNumber },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    const submitAsync = async () => {
      try {
        let targetExtensionId: number | string
        if (isAddMode) {
          const created = await createExtensionMutation.mutateAsync({
            type: ExtensionType.RING_GROUP,
            name: name.trim(),
            number: number.trim(),
            omit_from_directory: omitFromDirectory,
            ring_strategy: ringStrategy,
            custom_ringback_audio_id: ringGroupPlaylistId.trim()
              ? parseInt(ringGroupPlaylistId, 10)
              : null,
            ring_group_members: memberIds,
            members: memberIds,
          } as never)
          targetExtensionId = created.id
        } else {
          if (!extensionId) return
          targetExtensionId = extensionId
        }
        await updateRingGroupMutation.mutateAsync({
          extensionId: targetExtensionId,
          payload: {
            name: name.trim(),
            number: number.trim(),
            omit_from_directory: omitFromDirectory,
            ring_strategy: ringStrategy,
            custom_ringback_audio_id: ringGroupPlaylistId.trim()
              ? parseInt(ringGroupPlaylistId, 10)
              : null,
            ring_group_members: memberIds,
          },
        })
        toast.create({
          title: isAddMode
            ? text('extensions.edit.ringGroup.created', 'Ring group created')
            : text('extensions.edit.ringGroup.updated', 'Ring group updated'),
          type: 'success',
          duration: TOAST_DURATION_SUCCESS,
          closable: true,
        })
        navigate(listPath)
      } catch (error: unknown) {
        const apiErr = error as ApiError | undefined
        const message =
          apiErr?.message ??
          (error as Error)?.message ??
          text(
            'extensions.edit.ringGroup.saveFailed',
            'Failed to save ring group',
          )
        toast.create({
          title: isAddMode
            ? text('extensions.edit.createFailed', 'Create failed')
            : text('extensions.edit.updateFailed', 'Update failed'),
          description: message,
          type: 'error',
          duration: TOAST_DURATION_ERROR,
          closable: true,
        })
      }
    }
    submitAsync()
  }

  const isPending =
    updateExtensionMutation.isPending ||
    updateRingGroupMutation.isPending ||
    createExtensionMutation.isPending
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoading || !extension)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }

  if (
    !isAddMode &&
    (isError || !extension || extension.type !== ExtensionType.RING_GROUP)
  ) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text('extensions.edit.ringGroup.notType', 'Not a ring group')}
          </Heading>
          <Box mb={4}>
            {text(
              'extensions.edit.ringGroup.notTypeDescription',
              'This extension is not a ring group. Use the Extensions list to edit other types.',
            )}
          </Box>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  return renderWithWrapper(
    wrapper,
    breadcrumbs,
    <form onSubmit={handleSubmit}>
      <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>{typeLabel}</Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3} css={{ containerType: 'inline-size' }}>
            <Grid
              templateColumns="repeat(2, minmax(0, 1fr))"
              gap={4}
              css={{
                '@container (max-width: 640px)': {
                  gridTemplateColumns: 'minmax(0, 1fr)',
                },
              }}
            >
              <Field.Root required invalid={nameInvalid}>
                <Field.Label>
                  {text('extensions.columns.name', 'Name')}
                </Field.Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={text(
                    'extensions.edit.ringGroup.namePlaceholder',
                    'Ring group name',
                  )}
                  disabled={isPending}
                />
                {nameInvalid && (
                  <Field.ErrorText>
                    {text('extensions.edit.nameRequired', 'Name is required')}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root required invalid={numberInvalid}>
                <Field.Label>
                  {text('extensions.columns.number', 'Number')}
                </Field.Label>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder={text(
                    'extensions.edit.extensionNumberPlaceholder',
                    'Extension number',
                  )}
                  disabled={isPending}
                />
                {numberInvalid && (
                  <Field.ErrorText>
                    {text(
                      'extensions.edit.numberRequired',
                      'Number is required',
                    )}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root gridColumn="1 / -1">
                <Box display="flex" alignItems="center" gap={3}>
                  <Toggle
                    colorPalette="brand"
                    checked={omitFromDirectory}
                    onCheckedChange={(e) =>
                      setOmitFromDirectory(e.checked === true)
                    }
                    disabled={isPending}
                  >
                    {text(
                      'extensions.edit.omitFromDirectory',
                      'Omit from directory',
                    )}
                  </Toggle>
                </Box>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text('extensions.edit.options', 'Options')}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3} css={{ containerType: 'inline-size' }}>
            <Grid
              templateColumns="repeat(2, minmax(0, 1fr))"
              gap={4}
              css={{
                '@container (max-width: 640px)': {
                  gridTemplateColumns: 'minmax(0, 1fr)',
                },
              }}
            >
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.ringGroup.ringStrategy',
                    'Ring Strategy',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={ringStrategy}
                    onChange={(e) => setRingStrategy(e.target.value)}
                    px={3}
                  >
                    {RING_STRATEGY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value === 'Ring All'
                          ? text(
                              'extensions.edit.ringGroup.ringAll',
                              'Ring All',
                            )
                          : text(
                              'extensions.edit.ringGroup.pagingGroup',
                              'Paging Group',
                            )}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.ringGroup.ringbackAudio',
                    'Ringback Audio',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={ringGroupPlaylistId}
                    onChange={(e) => setRingGroupPlaylistId(e.target.value)}
                    px={3}
                  >
                    {ringbackPlaylistOptions.map((option) => (
                      <option
                        key={option.value || 'none'}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        {!isAddMode && (
          <Card.Root>
            <Card.Header p={6} pb={0}>
              <Card.Title>
                {text('extensions.edit.members', 'Members')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6} pt={3}>
              <PickList
                allItems={allLineItems}
                assignedIds={memberIds}
                onAssignedChange={setMemberIds}
                assignedLabels={Object.fromEntries(
                  (
                    extension as ExtensionInstance & RingGroupSeedFields
                  )?.members?.map((member) => [
                    member.id,
                    `${member.number} — ${member.name}`,
                  ]) ?? [],
                )}
                itemNoun={text('extensions.edit.membersNoun', 'members')}
                maxAssigned={RING_GROUP_MEMBER_LIMIT}
                disabled={isPending}
              />
            </Card.Body>
          </Card.Root>
        )}

        <Box pt={2} px={4} pb={3}>
          <Button
            type="submit"
            colorPalette="brand"
            loading={isPending}
            disabled={isPending}
            borderRadius="4px"
          >
            {isAddMode
              ? text(
                  'extensions.edit.ringGroup.createButton',
                  'Create Ring Group',
                )
              : text(
                  'extensions.edit.ringGroup.updateButton',
                  'Update Ring Group',
                )}
          </Button>
          <Button
            type="button"
            variant="outline"
            ml={3}
            borderRadius="4px"
            asChild
          >
            <Link to={listPath}>
              {text('extensions.common.cancel', 'Cancel')}
            </Link>
          </Button>
        </Box>
      </VStack>
    </form>,
  )
}
